import { useMutation, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { fundCallsApi } from 'src/Services/API';
import { CreateFundCallDto, SendFundCallDto, UpdateFundCallDto } from 'src/Services/API/generated';

/**
 * Envoyer un appel ou enregistrer un versement change les soldes : on invalide la même
 * famille de caches que les remboursements, pas seulement la liste des appels.
 */
const useInvalidateFundCalls = () => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(['fund-calls', assetId]);
    queryClient.invalidateQueries(['money-transfers', assetId]);
    queryClient.invalidateQueries(['fetch-expense-balance', assetId]);
    queryClient.invalidateQueries(['fetch-personal-wallet', assetId]);
  };
};

export const useCreateFundCall = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateFundCalls();

  return useMutation({
    mutationFn: async (dto: CreateFundCallDto) => {
      if (!assetId) throw new Error('No assetId provided');
      return (await fundCallsApi.fundCallsControllerCreate(assetId, dto)).data;
    },
    onSuccess: invalidate,
  });
};

export const useUpdateFundCall = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateFundCalls();

  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateFundCallDto }) => {
      if (!assetId) throw new Error('No assetId provided');
      return (await fundCallsApi.fundCallsControllerUpdate(assetId, id, dto)).data;
    },
    onSuccess: invalidate,
  });
};

export const useSendFundCall = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateFundCalls();

  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: SendFundCallDto }) => {
      if (!assetId) throw new Error('No assetId provided');
      return (await fundCallsApi.fundCallsControllerSend(assetId, id, dto)).data;
    },
    onSuccess: invalidate,
  });
};

export const useDeleteFundCall = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateFundCalls();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!assetId) throw new Error('No assetId provided');
      await fundCallsApi.fundCallsControllerRemove(assetId, id);
    },
    onSuccess: invalidate,
  });
};
